import { useState } from 'react';
import { Button } from '@react-apps/design-system';
import { Textarea } from '@/shared/components/textarea';
import { useInvoiceClosedForDuplicatePayment } from '../hooks/use-contract-details';
import { useReopenOperationalAlertForEntity } from '../hooks/use-sales-actions';
import {
  ErrorBanner,
  InfoNotice,
  SuccessBanner,
  formatDateTime,
  getLoadErrorMessage,
} from '../shared/display';

/** Mesmo mínimo do `ReopenSalesAlertForEntityRequestDto` no monolith. */
const MIN_NOTE = 3;

/**
 * Avisa, no resumo da fatura, que ela foi **encerrada** para pagamento
 * duplicado — um fechamento na Central de Pendências marcou "não abrir de novo
 * para esta fatura" — e deixa desfazer. É aqui, e não na Central, porque quem
 * suspeita de um pagamento novo olha a fatura, e a Central só lista abertos.
 *
 * Sem o encerramento, não renderiza nada — nem enquanto carrega: fica abaixo
 * do resumo, então aparecer depois não empurra o que o operador está lendo.
 */
export function InvoiceDuplicatePaymentSilence({
  invoiceId,
}: {
  invoiceId: string;
}) {
  const {
    data: alert,
    isError,
    refetch,
    isFetching,
  } = useInvoiceClosedForDuplicatePayment(invoiceId);
  const mutation = useReopenOperationalAlertForEntity(['sales']);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');

  if (mutation.isSuccess) {
    return (
      <SuccessBanner message="A fatura volta a ser vigiada: o próximo pagamento duplicado nela abre alerta na Central de Pendências." />
    );
  }
  if (isError) {
    return (
      <div className="space-y-2">
        <ErrorBanner message="Não foi possível conferir se esta fatura está encerrada para alertas de pagamento duplicado." />
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Conferindo…' : 'Tentar de novo'}
          </Button>
        </div>
      </div>
    );
  }
  if (!alert) return null;

  return (
    <div className="space-y-3">
      <InfoNotice>
        Pagamento duplicado nesta fatura <strong>não abre alerta</strong>: ela
        foi encerrada em {formatDateTime(alert.inThisStatusSince)}
        {alert.resolutionNote ? ` — “${alert.resolutionNote}”` : ''}.
      </InfoNotice>
      {mutation.isError && (
        <ErrorBanner
          // 4xx traz a regra (ex.: já reaberta); 5xx é texto técnico.
          message={getLoadErrorMessage(
            mutation.error,
            'Não foi possível voltar a vigiar a fatura. Nada foi alterado.',
          )}
        />
      )}
      {confirming ? (
        <>
          <Textarea
            label="Justificativa (obrigatória)"
            value={note}
            onChange={setNote}
            placeholder="Ex.: o cliente voltou a pagar em duplicidade"
            helperText="Fica registrada no histórico do alerta, junto com quem executou."
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={mutation.isPending}
            >
              Voltar
            </Button>
            <Button
              onClick={() =>
                mutation.mutate({ alertId: alert.id, note: note.trim() })
              }
              disabled={mutation.isPending || note.trim().length < MIN_NOTE}
            >
              {mutation.isPending ? 'Salvando…' : 'Voltar a vigiar'}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => setConfirming(true)}>
            Voltar a abrir alerta para esta fatura
          </Button>
        </div>
      )}
    </div>
  );
}
